import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  ExtendedStorageInstance,
  FirebaseReducer,
  firebaseReducer,
  getFirebase,
  actionTypes as rrfActionTypes,
} from 'react-redux-firebase';
import { firestoreReducer, constants as rfConstants } from 'redux-firestore';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { CountryMarkets, Customers } from '../../common/Customer/types';
import { Formulation } from '../../features/Recommendations/FormulationRecommendations';
import { Briefing } from '../../types/briefing';
import rootReducers from './rootReducers';

const extraArgument = {
  getFirebase,
};

export const reducer = combineReducers({
  ...rootReducers,
  firebase: firebaseReducer,
  firestore: firestoreReducer,
});

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
};

const persistedReducer = persistReducer(persistConfig, reducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          // just ignore every redux-firebase and react-redux-firebase action type
          ...Object.keys(rfConstants.actionTypes).map(
            (type) => `${rfConstants.actionsPrefix}/${type}`,
          ),
          ...Object.keys(rrfActionTypes).map(
            (type) => `@@reactReduxFirebase/${type}`,
          ),
        ],
        ignoredPaths: ['firebase', 'firestore'],
      },
      thunk: {
        extraArgument,
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export default store;
export const persistor = persistStore(store);

interface Profile {
  name: string;
  email: string;
}

export type Briefings = {
  [id: string]: Briefing;
};

export type Customer = {
  erp_id: string;
  name: string;
};

export type Features = { available: string[] };

export type User = {
  uid?: string;
  email: string;
  first_name: string;
  last_name: string;
  azelis_employee: boolean;
  am_email?: string;
  /**
   * @deprecated use `customer.erp_id` from `useAuth` instead, this doesn't respect the selected customer by the user
   */
  erp_id: string;
  countries_and_markets?: CountryMarkets;
  /**
   * @deprecated use `customer.customer_name` from `useAuth` instead, this doesn't respect the selected customer by the user
   */
  customer_name?: string;
  GUID?: string | null;
  invoice_guid?: string | null;
  customers: (string | null | undefined)[];
  loginExpires?: unknown;
};

interface Data {
  user: User;
  briefings: Briefings;
  customerBriefings: Briefings;
  customers: Customers;
  features: Features;
  formulations: Formulation[];
}

export type RootState = ReturnType<typeof store.getState> & {
  firebase: {
    auth: FirebaseReducer.AuthState;
    profile: Profile;
    storage: ExtendedStorageInstance;
  };
  firestore: { data: Data };
};
